import React, { useEffect, useState, useMemo, useCallback } from "react";
import TableListTasks from "@/shared/components/organisms/TableListTasks";
import {
  transformApiDataToTasksPageProps,
  TasksPageProps,
} from "./transformApiDataToTasksPageProps";
import NavbarFilter from "@/shared/components/organisms/NavbarFilter";
import { InputWithCornerHint } from "@/shared/components/atoms/InputWithCornerHint";
import { t } from "i18next";
import { Select } from "@/shared/components/organisms/Select";
import KeywordManager from "./KeywordManager";
import { useGetAllTasksQuery } from "./api/taskApi";
import { useParams } from "react-router-dom";
import StatsUI from "@/shared/components/molecules/StatsUI";

// Definición de interfaces
interface SelectOption {
  id: string | number;
  name: string;
}

interface StatItemProps {
  name: string;
  stat: number | string;
  percentage: string;
  days?: number;
}

// Opciones estáticas para el filtro de estado
const statusOptions: SelectOption[] = [
  { id: "all", name: t("globals.all", "Todos") },
  { id: "Pendiente", name: t("task.views.status.pending", "Pendiente") },
  { id: "Completada", name: t("task.views.status.completed", "Completada") },
];

const TasksPage = () => {
  // Estados del componente
  const [transformedTasks, setTransformedTasks] = useState<TasksPageProps[]>(
    []
  );
  const [openFilters, setOpenFilters] = useState<boolean>(false);
  const [filterTaskName, setFilterTaskName] = useState<string>("");
  const [navbarSearchText, setNavbarSearchText] = useState<string>("");
  const [filterStatus, setFilterStatus] = useState<string>("all");
  const [filterAssignedTo, setFilterAssignedTo] = useState<string>("all");
  const [filterLeadUser, setFilterLeadUser] = useState<string>("all");
  const [filterStartDate, setFilterStartDate] = useState<string>("");
  const [filterEndDate, setFilterEndDate] = useState<string>("");
  const [pagination, setPagination] = useState<{
    currentPage: number;
    totalPages: number;
    pageSize: number;
    totalResults: number;
  }>({
    currentPage: 1,
    totalPages: 1,
    pageSize: 15,
    totalResults: 0,
  });
  const [openKpis, setOpenKpis] = useState<boolean>(false);
  const [openKeyword, setOpenKeyword] = useState<boolean>(false);

  // Obtener el branch_id desde los parámetros de la URL
  const { id: branch_id } = useParams<{ id: string }>();

  // Consulta a la API para obtener todas las tareas
  const { data, isLoading, error } = useGetAllTasksQuery({ branch_id });

  // Funciones para alternar visibilidad
  const toggleDrawer = useCallback(() => {
    setOpenFilters((prev) => !prev);
  }, []);

  const toggleKpis = useCallback(() => {
    setOpenKpis((prev) => !prev);
  }, []);

  // Datos crudos de las tareas desde la API
  const allTasksRaw = data?.data || [];

  // Opciones dinámicas para el filtro "Asignado a"
  const assignedToOptions = useMemo((): SelectOption[] => {
    if (!allTasksRaw.length)
      return [{ id: "all", name: t("globals.all", "Todos") }];
    const uniqueAssigned = Array.from(
      new Set(allTasksRaw.map((task) => task.assigned?.name).filter(Boolean))
    )
      .sort()
      .map((name) => ({ id: name as string, name: name as string }));
    return [{ id: "all", name: t("globals.all", "Todos") }, ...uniqueAssigned];
  }, [allTasksRaw]);

  // Opciones dinámicas para el filtro "Lead/User"
  const leadUserOptions = useMemo((): SelectOption[] => {
    if (!allTasksRaw.length)
      return [{ id: "all", name: t("globals.all", "Todos") }];
    const uniqueLeadUsers = Array.from(
      new Set(allTasksRaw.map((task) => task.user).filter(Boolean))
    )
      .sort()
      .map((name) => ({ id: name as string, name: name as string }));
    return [{ id: "all", name: t("globals.all", "Todos") }, ...uniqueLeadUsers];
  }, [allTasksRaw]);

  // Efecto para filtrar tareas y actualizar paginación
  useEffect(() => {
    let processedTasks = transformApiDataToTasksPageProps(allTasksRaw);

    // Normalizar y aplicar filtros acumulativamente
    processedTasks = processedTasks.filter((task) => {
      // Normalizar status para evitar problemas con mayúsculas/minúsculas
      const taskStatus = task.status?.trim().toLowerCase();
      const filterStatusNormalized =
        filterStatus !== "all" ? filterStatus.toLowerCase() : "all";

      // Aplicar todos los filtros
      const matchesName =
        (!navbarSearchText && !filterTaskName) ||
        (task.name &&
          (navbarSearchText
            ? task.name.toLowerCase().includes(navbarSearchText.toLowerCase())
            : true) &&
          (filterTaskName
            ? task.name.toLowerCase().includes(filterTaskName.toLowerCase())
            : true));

      const matchesStatus =
        filterStatusNormalized === "all" ||
        taskStatus === filterStatusNormalized;

      const matchesAssigned =
        filterAssignedTo === "all" || task.assigned?.name === filterAssignedTo;

      const matchesLead =
        filterLeadUser === "all" || task.user === filterLeadUser;

      const matchesStartDate =
        !filterStartDate ||
        (task.date && new Date(task.date) >= new Date(filterStartDate));

      const matchesEndDate =
        !filterEndDate ||
        (task.date && new Date(task.date) <= new Date(filterEndDate));

      return (
        matchesName &&
        matchesStatus &&
        matchesAssigned &&
        matchesLead &&
        matchesStartDate &&
        matchesEndDate
      );
    });

    // Depuración: inspeccionar tareas filtradas
    console.log("Tareas filtradas:", processedTasks);

    // Actualizar paginación
    const totalResults = processedTasks.length;
    const totalPages = Math.ceil(totalResults / pagination.pageSize) || 1;
    const currentPage = Math.min(pagination.currentPage, totalPages);

    setTransformedTasks(processedTasks);
    setPagination((prev) => ({
      ...prev,
      totalResults,
      totalPages,
      currentPage,
    }));
  }, [
    allTasksRaw,
    navbarSearchText,
    filterTaskName,
    filterStatus,
    filterAssignedTo,
    filterLeadUser,
    filterStartDate,
    filterEndDate,
    pagination.pageSize,
  ]);

  // Cálculo de KPIs basado en tareas filtradas o todas las tareas
  const statsUI = useMemo(() => {
    // Determinar si hay filtros activos
    const hasFilters =
      navbarSearchText ||
      filterTaskName ||
      filterStatus !== "all" ||
      filterAssignedTo !== "all" ||
      filterLeadUser !== "all" ||
      filterStartDate ||
      filterEndDate;

    // Usar tareas filtradas si hay filtros, o todas las tareas si no los hay
    const tasksToProcess = hasFilters
      ? transformedTasks
      : transformApiDataToTasksPageProps(allTasksRaw);

    const totalTasks = tasksToProcess.length;
    const today = new Date();

    let completedOnTime = 0;
    let completedLate = 0;
    let notCompleted = 0;
    let daysLateNotCompleted = 0;

    // Procesar las tareas seleccionadas
    tasksToProcess.forEach((task) => {
      // Validar fechas
      const taskDate = task.date ? new Date(task.date) : null;

      if (!taskDate) return;

      const daysDiff = (date1: Date, date2: Date) =>
        Math.floor((date1.getTime() - date2.getTime()) / (1000 * 60 * 60 * 24));

      // Normalizar status
      const taskStatus = task.status?.trim().toLowerCase();

      if (taskStatus === "completada") {
        // Sin completion_date, asumimos que todas las tareas completadas son a tiempo
        completedOnTime++;
      } else if (taskStatus === "pendiente") {
        notCompleted++;
        if (taskDate < today) {
          daysLateNotCompleted += daysDiff(today, taskDate);
        }
      }
    });

    const percentage = (count: number) =>
      totalTasks ? ((count / totalTasks) * 100).toFixed(0) + "%" : "0%";

    // Definir los KPIs
    const kpis: StatItemProps[] = [
      {
        name: t(
          "task.views.kpis.completedOnTime",
          "Tareas completadas (a tiempo)"
        ),
        stat: completedOnTime,
        percentage: percentage(completedOnTime),
      },
      {
        name: t("task.views.kpis.completedLate", "Tareas completadas (tarde)"),
        stat: completedLate,
        percentage: percentage(completedLate),
        // No se incluyen días de retraso para completadas tarde, ya que no hay completion_date
      },
      {
        name: t("task.views.kpis.notCompleted", "Tareas no completadas"),
        stat: notCompleted,
        percentage: percentage(notCompleted),
        days: daysLateNotCompleted > 0 ? daysLateNotCompleted : undefined,
      },
    ];

    // Depuración: inspeccionar KPIs
    console.log("KPIs calculados:", kpis);

    return kpis;
  }, [
    allTasksRaw,
    transformedTasks,
    navbarSearchText,
    filterTaskName,
    filterStatus,
    filterAssignedTo,
    filterLeadUser,
    filterStartDate,
    filterEndDate,
  ]);

  // Función para cambiar de página
  const handlePageChange = useCallback((newPage: number) => {
    setPagination((prev) => ({ ...prev, currentPage: newPage }));
  }, []);

  // Manejo de errores
  if (error) {
    return (
      <p className="text-red-500 p-4">
        {t("errors.loadTasks", "Error al cargar las tareas.")}
      </p>
    );
  }

  return (
    <div className="p-4 md:p-6 overflow-auto">
      {/* Barra de navegación con búsqueda */}
      <div className="flex flex-1 self-stretch lg:gap-x-2 px-4 pb-2 border-b-2">
        <NavbarFilter
          filter={true}
          text_botton={"Etiquetas"}
          formComponents={
            <InputWithCornerHint
              label={t("task.views.searchTask", "Buscar Tarea")}
              span=""
              id="navbarSearch"
              name="navbarSearch"
              type="text"
              placeholder={t("globals.search", "Buscar...")}
              value={navbarSearchText}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setNavbarSearchText(e.target.value)
              }
            />
          }
          OnClickPrimaryButton={() => setOpenKeyword(!openKeyword)}
          toggleDrawer={toggleDrawer}
          toggleKpis={toggleKpis}
          optionsMenu={[]}
          showIcons={true}
        />
      </div>

      {/* Modal de KPIs */}
      {openKpis && (
        <div className="fixed inset-5 md:inset-10 xl:inset-x-64  h-min xl:w-10/12 z-50 flex">
          <div
            className="fixed inset-0 bg-black opacity-50"
            onClick={toggleKpis}
          ></div>
          <div className="relative bg-white w-full shadow-xl">
            <div className="md:px-8 py-4">
              <StatsUI title="KPIs" stats={statsUI} onClose={toggleKpis} />
            </div>
          </div>
        </div>
      )}

      {/* Drawer de filtros */}
      {openFilters && (
        <div className="fixed inset-5 md:inset-10 xl:inset-x-64  h-min xl:w-10/12 z-50 flex">
          <div
            className="fixed inset-0 bg-black opacity-50"
            onClick={toggleDrawer}
          ></div>
          <div className="relative bg-white w-full shadow-xl">
            <div className="p-4">
              <div className="flex justify-between items-center mb-4 border-b pb-2">
                <h2 className="text-lg font-semibold text-gray-700">
                  {t("task.views.filters", "Filtros")}
                </h2>
                <button
                  onClick={toggleDrawer}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-6 w-6"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>
              <div className="flex flex-wrap gap-4">
                <div className="flex-grow basis-full sm:basis-[calc(50%-0.5rem)] md:basis-[calc(33.33%-0.66rem)] lg:basis-[calc(25%-0.75rem)]">
                  <InputWithCornerHint
                    label={t("task.views.taskName", "Nombre de Tarea")}
                    id="drawerFilterTaskName"
                    name="drawerFilterTaskName"
                    type="text"
                    placeholder={t(
                      "task.views.searchByName",
                      "Buscar por nombre..."
                    )}
                    value={filterTaskName}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      setFilterTaskName(e.target.value)
                    }
                  />
                </div>
                <div className="flex-grow basis-full sm:basis-[calc(50%-0.5rem)] md:basis-[calc(33.33%-0.66rem)] lg:basis-[calc(25%-0.75rem)]">
                  <Select
                    options={statusOptions}
                    selected={
                      statusOptions.find((opt) => opt.id === filterStatus) ||
                      statusOptions[0]
                    }
                    setSelected={(option) =>
                      setFilterStatus(option.id as string)
                    }
                    tittle={t("task.views.status.title", "Estado")}
                    name="drawerFilterStatus"
                  />
                </div>
                <div className="flex-grow basis-full sm:basis-[calc(50%-0.5rem)] md:basis-[calc(33.33%-0.66rem)] lg:basis-[calc(25%-0.75rem)]">
                  <Select
                    options={assignedToOptions}
                    selected={
                      assignedToOptions.find(
                        (opt) => opt.id === filterAssignedTo
                      ) || assignedToOptions[0]
                    }
                    setSelected={(option) =>
                      setFilterAssignedTo(option.id as string)
                    }
                    tittle={t("task.views.assignedTo", "Asignado a")}
                    name="drawerFilterAssignedTo"
                  />
                </div>
                <div className="flex-grow basis-full sm:basis-[calc(50%-0.5rem)] md:basis-[calc(33.33%-0.66rem)] lg:basis-[calc(25%-0.75rem)]">
                  <Select
                    options={leadUserOptions}
                    selected={
                      leadUserOptions.find(
                        (opt) => opt.id === filterLeadUser
                      ) || leadUserOptions[0]
                    }
                    setSelected={(option) =>
                      setFilterLeadUser(option.id as string)
                    }
                    tittle={t("task.views.leadUser", "Lead/User")}
                    name="drawerFilterLeadUser"
                  />
                </div>
                <div className="flex-grow basis-full sm:basis-[calc(50%-0.5rem)] md:basis-[calc(33.33%-0.66rem)] lg:basis-[calc(25%-0.75rem)]">
                  <InputWithCornerHint
                    label={t("globals.startDate", "Fecha Inicio")}
                    id="drawerFilterStartDate"
                    name="drawerFilterStartDate"
                    type="date"
                    value={filterStartDate}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      setFilterStartDate(e.target.value)
                    }
                  />
                </div>
                <div className="flex-grow basis-full sm:basis-[calc(50%-0.5rem)] md:basis-[calc(33.33%-0.66rem)] lg:basis-[calc(25%-0.75rem)]">
                  <InputWithCornerHint
                    label={t("globals.endDate", "Fecha Final")}
                    id="drawerFilterEndDate"
                    name="drawerFilterEndDate"
                    type="date"
                    value={filterEndDate}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      setFilterEndDate(e.target.value)
                    }
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Renderizado condicional */}
      {isLoading && !transformedTasks.length ? (
        <p className="text-center p-4">
          {t("globals.loading", "Cargando tareas...")}
        </p>
      ) : !isLoading &&
        transformedTasks.length === 0 &&
        (navbarSearchText ||
          filterTaskName ||
          filterStatus !== "all" ||
          filterAssignedTo !== "all" ||
          filterLeadUser !== "all" ||
          filterStartDate ||
          filterEndDate) ? (
        <p className="text-center p-4 text-gray-500">
          {t(
            "task.views.noTasksFoundWithFilters",
            "No se encontraron tareas con los filtros aplicados."
          )}
        </p>
      ) : !isLoading && allTasksRaw.length === 0 ? (
        <p className="text-center p-4 text-gray-500">
          {t("task.views.noTasksAvailable", "No hay tareas disponibles.")}
        </p>
      ) : (
        <TableListTasks
          people={transformedTasks.slice(
            (pagination.currentPage - 1) * pagination.pageSize,
            pagination.currentPage * pagination.pageSize
          )}
          currentPage={pagination.currentPage}
          totalPages={pagination.totalPages}
          onPageChange={handlePageChange}
          pageSize={pagination.pageSize}
          totalResults={pagination.totalResults}
        />
      )}

      {/* Gestor de palabras clave */}
      <KeywordManager
        setOpen={setOpenKeyword}
        open={openKeyword}
        //branch={branch_id}
      />
    </div>
  );
};

export default TasksPage;
